import React from 'react';
import {
  Table, Button, Modal, Form, Input, Select, Space, Tag,
  Card, Empty, message, Divider, Typography, Switch, InputNumber, QRCode, Collapse, Alert, Grid, Steps, Upload, List
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, ReloadOutlined,
  ShareAltOutlined, KeyOutlined, LinkOutlined, UserAddOutlined,
  CopyOutlined, DownloadOutlined, InboxOutlined, FileTextOutlined
} from '@ant-design/icons';
import axios from 'axios';
import Esp32PinMap from '../components/Esp32PinMap';

const { Text, Paragraph } = Typography;
const { useBreakpoint } = Grid;

function authHeader() {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function parseDeviceTimestampMs(value) {
  if (!value) return NaN;
  const raw = String(value).trim();
  const normalized = raw.includes('T') ? raw : raw.replace(' ', 'T');
  const withZone = /Z$|[+-]\d{2}:?\d{2}$/.test(normalized) ? normalized : `${normalized}Z`;
  const ts = Date.parse(withZone);
  if (Number.isFinite(ts)) return ts;
  const fallback = Date.parse(raw);
  return Number.isFinite(fallback) ? fallback : NaN;
}

function formatBytes(bytes) {
  const n = Number(bytes) || 0;
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

const USE_CASE_TEMPLATES = {
  lighting: {
    label: 'Iluminacion y relay',
    description: 'Control de luces y un interruptor de pared',
    type: 'relay',
    telemetrySeconds: 10,
    channels: [
      { name: 'led', direction: 'output', signal: 'digital', pin: 2, inverted: false, pullup: false },
      { name: 'relay1', direction: 'output', signal: 'digital', pin: 5, inverted: false, pullup: false },
      { name: 'switch1', direction: 'input', signal: 'digital', pin: 4, inverted: false, pullup: true },
    ]
  },
  irrigation: {
    label: 'Riego automatico',
    description: 'Valvulas y lectura de humedad de suelo',
    type: 'relay',
    telemetrySeconds: 20,
    channels: [
      { name: 'led', direction: 'output', signal: 'digital', pin: 2, inverted: false, pullup: false },
      { name: 'valve1', direction: 'output', signal: 'digital', pin: 18, inverted: false, pullup: false },
      { name: 'valve2', direction: 'output', signal: 'digital', pin: 19, inverted: false, pullup: false },
      { name: 'soil', direction: 'input', signal: 'analog', pin: 34, inverted: false, pullup: false },
    ]
  },
  climate: {
    label: 'Clima y ventilacion',
    description: 'Control de ventilador y lectura de sensores',
    type: 'sensor',
    telemetrySeconds: 15,
    channels: [
      { name: 'led', direction: 'output', signal: 'digital', pin: 2, inverted: false, pullup: false },
      { name: 'fan', direction: 'output', signal: 'digital', pin: 16, inverted: false, pullup: false },
      { name: 'temp_probe', direction: 'input', signal: 'analog', pin: 35, inverted: false, pullup: false },
      { name: 'humidity_probe', direction: 'input', signal: 'analog', pin: 32, inverted: false, pullup: false },
    ]
  },
  security: {
    label: 'Seguridad',
    description: 'Sensor de puerta, movimiento y sirena',
    type: 'multi',
    telemetrySeconds: 8,
    channels: [
      { name: 'led', direction: 'output', signal: 'digital', pin: 2, inverted: false, pullup: false },
      { name: 'siren', direction: 'output', signal: 'digital', pin: 23, inverted: false, pullup: false },
      { name: 'door', direction: 'input', signal: 'digital', pin: 14, inverted: false, pullup: true },
      { name: 'pir', direction: 'input', signal: 'digital', pin: 27, inverted: false, pullup: false },
    ]
  },
  custom: {
    label: 'Personalizado',
    description: 'Comienza con base minima y ajusta manualmente',
    type: 'generic',
    telemetrySeconds: 10,
    channels: [
      { name: 'led', direction: 'output', signal: 'digital', pin: 2, inverted: false, pullup: false },
    ]
  }
};

export default function Devices() {
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const [devices, setDevices] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [nowTs, setNowTs] = React.useState(Date.now());
  const [modalVisible, setModalVisible] = React.useState(false);
  const [editingDevice, setEditingDevice] = React.useState(null);
  const [form] = Form.useForm();
  const [shareModalVisible, setShareModalVisible] = React.useState(false);
  const [sharingDevice, setSharingDevice] = React.useState(null);
  const [shareForm] = Form.useForm();
  const [shareResult, setShareResult] = React.useState(null);
  const [shareLoading, setShareLoading] = React.useState(false);
  const [joinModalVisible, setJoinModalVisible] = React.useState(false);
  const [joinForm] = Form.useForm();
  const [joinLoading, setJoinLoading] = React.useState(false);
  const [otaModalVisible, setOtaModalVisible] = React.useState(false);
  const [otaTargetDevice, setOtaTargetDevice] = React.useState(null);
  const [otaLoading, setOtaLoading] = React.useState(false);
  const [otaForm] = Form.useForm();
  const [provisioningLoaded, setProvisioningLoaded] = React.useState(false);
  const watchedDeviceId = Form.useWatch('device_id', form) || 'ESP32-SALON-01';
  const [espConfig, setEspConfig] = React.useState({
    wifiSsid: 'TU_WIFI',
    wifiPassword: 'TU_CLAVE_WIFI',
    mqttHost: 'domotica.nc3civil.cl',
    mqttPort: 1883,
    mqttUser: 'vps_user_nc3',
    mqttPassword: '@Ramitas0810',
    deviceId: 'ESP32-SALON-01',
    ledPin: 2,
    telemetrySeconds: 10,
    sensorMode: 'none',
    i2cSda: 21,
    i2cScl: 22,
    bmpAddress: 'auto',
  });
  const [ioChannels, setIoChannels] = React.useState([
    { id: 1, name: 'led', direction: 'output', signal: 'digital', pin: 2, inverted: false },
    { id: 2, name: 'btn1', direction: 'input', signal: 'digital', pin: 4, pullup: true },
  ]);
  const [wizardStep, setWizardStep] = React.useState(0);
  const [useCase, setUseCase] = React.useState('lighting');
  const [projectName, setProjectName] = React.useState('Proyecto ESP32');
  const [codeBankFiles, setCodeBankFiles] = React.useState(() => {
    try {
      const raw = localStorage.getItem('codeBankFiles');
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });

  React.useEffect(() => {
    localStorage.setItem('codeBankFiles', JSON.stringify(codeBankFiles));
  }, [codeBankFiles]);

  const addCodeBankFile = React.useCallback((file) => {
    const id = `${file.name}-${file.size}-${file.lastModified}`;
    setCodeBankFiles((prev) => {
      if (prev.some((x) => x.id === id)) return prev;
      return [
        {
          id,
          name: file.name,
          size: Number(file.size) || 0,
          type: file.type || 'application/octet-stream',
          addedAt: new Date().toISOString(),
        },
        ...prev,
      ];
    });
  }, []);

  const removeCodeBankFile = React.useCallback((id) => {
    setCodeBankFiles((prev) => prev.filter((f) => f.id !== id));
  }, []);

  React.useEffect(() => {
    setEspConfig((prev) => ({ ...prev, deviceId: watchedDeviceId || 'ESP32-SALON-01' }));
  }, [watchedDeviceId]);

  const applyUseCaseTemplate = React.useCallback((key) => {
    const tpl = USE_CASE_TEMPLATES[key] || USE_CASE_TEMPLATES.custom;
    setUseCase(key);
    setIoChannels(tpl.channels.map((c, idx) => ({ ...c, id: Date.now() + idx })));
    form.setFieldsValue({ type: tpl.type });
    setEspConfig((prev) => ({
      ...prev,
      telemetrySeconds: tpl.telemetrySeconds,
      ledPin: tpl.channels[0]?.pin || prev.ledPin,
      sensorMode: key === 'climate' ? 'aht20_bmp280' : prev.sensorMode === 'aht20_bmp280' ? 'none' : prev.sensorMode,
    }));
  }, [form]);

  React.useEffect(() => {
    if (!modalVisible || editingDevice) return;
    if (wizardStep === 0) {
      applyUseCaseTemplate(useCase || 'lighting');
    }
  }, [modalVisible, editingDevice]);

  React.useEffect(() => {
    if (!modalVisible || editingDevice) return;
    const deviceId = (espConfig.deviceId || '').trim();
    if (!deviceId) return;

    const current = form.getFieldsValue(['device_id', 'name', 'type', 'mqtt_topic']);
    const nextValues = {};

    if (current.device_id !== deviceId) {
      nextValues.device_id = deviceId;
    }

    const prevAutoTopic = current.device_id ? `esp32/${current.device_id}/telemetry` : '';
    if (!current.mqtt_topic || current.mqtt_topic === prevAutoTopic) {
      nextValues.mqtt_topic = `esp32/${deviceId}/telemetry`;
    }

    if (!current.name) {
      nextValues.name = `ESP32 ${deviceId}`;
    }

    if (!current.type) {
      nextValues.type = 'relay';
    }

    if (Object.keys(nextValues).length > 0) {
      form.setFieldsValue(nextValues);
    }
  }, [espConfig.deviceId, modalVisible, editingDevice, form]);

  React.useEffect(() => {
    if (!modalVisible || editingDevice || provisioningLoaded) return;
    const loadProvisioning = async () => {
      try {
        const res = await axios.get('/api/auth/provisioning', { headers: authHeader() });
        const mqtt = res.data?.mqtt || {};
        const defaults = res.data?.defaults || {};
        setEspConfig((prev) => ({
          ...prev,
          mqttHost: mqtt.host || prev.mqttHost,
          mqttPort: Number(mqtt.port) || prev.mqttPort,
          mqttUser: mqtt.username || prev.mqttUser,
          mqttPassword: mqtt.password || prev.mqttPassword,
          ledPin: Number(defaults.led_pin) || prev.ledPin,
          telemetrySeconds: Number(defaults.telemetry_seconds) || prev.telemetrySeconds,
        }));
        setProvisioningLoaded(true);
      } catch {
        // Si falla el endpoint, se mantienen los defaults locales del generador.
      }
    };
    loadProvisioning();
  }, [modalVisible, editingDevice, provisioningLoaded]);

  const generatedEsp32Code = React.useMemo(() => {
    const esc = (value) => String(value ?? '').replace(/\\/g, '\\\\').replace(/"/g, '\\"');
    const cfg = {
      wifiSsid: esc(espConfig.wifiSsid),
      wifiPassword: esc(espConfig.wifiPassword),
      mqttHost: esc(espConfig.mqttHost),
      mqttPort: Number(espConfig.mqttPort) || 1883,
      mqttUser: esc(espConfig.mqttUser),
      mqttPassword: esc(espConfig.mqttPassword),
      deviceId: esc(espConfig.deviceId || 'ESP32-SALON-01'),
      ledPin: Number(espConfig.ledPin) || 2,
      telemetryMs: Math.max(3, Number(espConfig.telemetrySeconds) || 10) * 1000,
      sensorMode: espConfig.sensorMode === 'aht20_bmp280' ? 'aht20_bmp280' : 'none',
      i2cSda: Number(espConfig.i2cSda) || 21,
      i2cScl: Number(espConfig.i2cScl) || 22,
      bmpAddress: String(espConfig.bmpAddress || 'auto'),
    };

    const withAhtBmp = cfg.sensorMode === 'aht20_bmp280';

    const safeName = (value, fallback) => {
      const cleaned = String(value || '').trim().toLowerCase().replace(/[^a-z0-9_]/g, '_');
      return cleaned || fallback;
    };

    const channels = ioChannels.map((c, idx) => ({
      name: safeName(c.name, `ch_${idx + 1}`),
      direction: c.direction === 'input' ? 'input' : 'output',
      signal: c.signal === 'analog' ? 'analog' : 'digital',
      pin: Number(c.pin) || 0,
      inverted: !!c.inverted,
      pullup: !!c.pullup,
    }));

    const outputChannels = [
      { name: 'led', pin: cfg.ledPin, inverted: false },
      ...channels.filter((c) => c.direction === 'output' && c.name !== 'led')
    ];
    const inputChannels = channels.filter((c) => c.direction === 'input');

    const outputArrayCode = outputChannels.map((c) =>
      `  {"${c.name}", ${c.pin}, ${c.inverted ? 'true' : 'false'}, false}`
    ).join(',\n');
    const inputArrayCode = inputChannels.length
      ? inputChannels.map((c) =>
          `  {"${c.name}", ${c.pin}, ${c.signal === 'analog' ? 'true' : 'false'}, ${c.pullup ? 'true' : 'false'}}`
        ).join(',\n')
      : '  // Sin entradas adicionales';

    return `#include <WiFi.h>
  #include <WiFiClientSecure.h>
  #include <HTTPClient.h>
  #include <Update.h>
#include <PubSubClient.h>
#include <ArduinoJson.h>
${withAhtBmp ? '#include <Wire.h>\n#include <Adafruit_AHTX0.h>\n#include <Adafruit_BMP280.h>' : ''}

const char* WIFI_SSID = "${cfg.wifiSsid}";
const char* WIFI_PASS = "${cfg.wifiPassword}";
const char* MQTT_HOST = "${cfg.mqttHost}";
const int MQTT_PORT = ${cfg.mqttPort};
const char* MQTT_USER = "${cfg.mqttUser}";
const char* MQTT_PASS = "${cfg.mqttPassword}";
const char* DEVICE_ID = "${cfg.deviceId}";
const int LED_PIN = ${cfg.ledPin};

WiFiClient espClient;
PubSubClient mqtt(espClient);
${withAhtBmp ? 'Adafruit_AHTX0 aht;\nAdafruit_BMP280 bmp;' : ''}

String telemetryTopic = String("esp32/") + DEVICE_ID + "/telemetry";
String controlTopic = String("esp32/") + DEVICE_ID + "/control";
String statusTopic = String("esp32/") + DEVICE_ID + "/status";

struct OutputChannel {
  const char* name;
  int pin;
  bool inverted;
  bool state;
};

OutputChannel outputs[] = {
${outputArrayCode}
};
const int OUTPUT_COUNT = sizeof(outputs) / sizeof(outputs[0]);

struct InputChannel {
  const char* name;
  int pin;
  bool analog;
  bool pullup;
};

InputChannel inputs[] = {
${inputArrayCode}
};
const int INPUT_COUNT = sizeof(inputs) / sizeof(inputs[0]);

bool ledState = false;
unsigned long lastTelemetry = 0;
unsigned long telemetryIntervalMs = ${cfg.telemetryMs}UL;
unsigned long reconnectDelayMs = 2000;
String otaStatus = "idle";
${withAhtBmp ? 'bool ahtOk = false;\nbool bmpOk = false;\nfloat lastTemp = NAN;\nfloat lastHum = NAN;\nfloat lastPres = NAN;' : ''}

void logLine(const String& text) {
  Serial.println("[ESP32] " + text);
}

String buildClientId() {
  uint64_t mac = ESP.getEfuseMac();
  char suffix[13];
  snprintf(suffix, sizeof(suffix), "%04X%08X", (uint16_t)(mac >> 32), (uint32_t)mac);
  return String(DEVICE_ID) + "-" + suffix;
}

bool setOutputByName(const String& name, bool state) {
  for (int i = 0; i < OUTPUT_COUNT; i++) {
    if (name == outputs[i].name) {
      outputs[i].state = state;
      bool physical = outputs[i].inverted ? !state : state;
      digitalWrite(outputs[i].pin, physical ? HIGH : LOW);
      if (name == "led") ledState = state;
      return true;
    }
  }
  return false;
}

void appendIOState(StaticJsonDocument<256>& doc) {
  for (int i = 0; i < OUTPUT_COUNT; i++) {
    String key = String("out_") + outputs[i].name;
    doc[key] = outputs[i].state ? 1 : 0;
  }
  for (int i = 0; i < INPUT_COUNT; i++) {
    String key = String("in_") + inputs[i].name;
    if (inputs[i].analog) doc[key] = analogRead(inputs[i].pin);
    else doc[key] = digitalRead(inputs[i].pin);
  }
}

${withAhtBmp ? `void readSensors() {
  if (ahtOk) {
    sensors_event_t humidity, temp;
    aht.getEvent(&humidity, &temp);
    lastTemp = temp.temperature;
    lastHum = humidity.relative_humidity;
  }

  if (bmpOk) {
    lastPres = bmp.readPressure() / 100.0F;
  }
}
` : ''}

void publishState(const char* status = "online") {
  StaticJsonDocument<320> doc;
  doc["status"] = status;
  doc["led"] = ledState ? 1 : 0;
  doc["ota"] = otaStatus;
  doc["rssi"] = WiFi.RSSI();
  doc["uptime"] = millis() / 1000;
  doc["telemetry_ms"] = telemetryIntervalMs;
${withAhtBmp ? `  if (!isnan(lastTemp)) {
    doc["temp"] = lastTemp;
    doc["temperature"] = lastTemp;
    doc["in_temp_probe"] = lastTemp;
  }
  if (!isnan(lastHum)) {
    doc["hum"] = lastHum;
    doc["humidity"] = lastHum;
    doc["in_humidity_probe"] = lastHum;
  }
  if (!isnan(lastPres)) {
    doc["pres"] = lastPres;
    doc["pressure"] = lastPres;
    doc["in_pressure_probe"] = lastPres;
  }
  doc["sensor_aht"] = ahtOk ? 1 : 0;
  doc["sensor_bmp"] = bmpOk ? 1 : 0;
` : ''}
  appendIOState(doc);

  char payload[320];
  serializeJson(doc, payload);
  mqtt.publish(telemetryTopic.c_str(), payload, true);
  mqtt.publish(statusTopic.c_str(), payload, true);
  logLine(String("Publish status=") + status + " led=" + (ledState ? "1" : "0") + " intervalMs=" + telemetryIntervalMs + " topic=" + telemetryTopic);
}

bool performOta(const String& firmwareUrl) {
  if (firmwareUrl.length() < 8) {
    otaStatus = "invalid_url";
    publishState("online");
    return false;
  }

  logLine(String("OTA start: ") + firmwareUrl);
  otaStatus = "downloading";
  publishState("online");

  WiFiClientSecure secureClient;
  secureClient.setInsecure();
  HTTPClient http;

  bool useSecure = firmwareUrl.startsWith("https://");
  bool started = useSecure ? http.begin(secureClient, firmwareUrl) : http.begin(firmwareUrl);
  if (!started) {
    otaStatus = "http_begin_failed";
    publishState("online");
    return false;
  }

  int httpCode = http.GET();
  if (httpCode != HTTP_CODE_OK) {
    logLine(String("OTA HTTP error: ") + httpCode);
    otaStatus = "http_error";
    http.end();
    publishState("online");
    return false;
  }

  int contentLength = http.getSize();
  WiFiClient* stream = http.getStreamPtr();

  if (!Update.begin(contentLength > 0 ? contentLength : UPDATE_SIZE_UNKNOWN)) {
    logLine("OTA Update.begin failed");
    otaStatus = "begin_failed";
    http.end();
    publishState("online");
    return false;
  }

  size_t written = Update.writeStream(*stream);
  if (contentLength > 0 && written != (size_t)contentLength) {
    logLine(String("OTA written mismatch: ") + written + "/" + contentLength);
    otaStatus = "write_mismatch";
    Update.end();
    http.end();
    publishState("online");
    return false;
  }

  if (!Update.end() || !Update.isFinished()) {
    logLine(String("OTA end failed err=") + Update.getError());
    otaStatus = "update_failed";
    http.end();
    publishState("online");
    return false;
  }

  http.end();
  otaStatus = "ok_restarting";
  publishState("online");
  logLine("OTA success. Restarting...");
  delay(1200);
  ESP.restart();
  return true;
}

void setLed(bool state) {
  setOutputByName("led", state);
  logLine(String("LED -> ") + (state ? "ON" : "OFF") + " (GPIO " + String(LED_PIN) + ")");
  publishState();
}

void connectWiFi() {
  logLine(String("Conectando WiFi SSID: ") + WIFI_SSID);
  WiFi.mode(WIFI_STA);
  WiFi.begin(WIFI_SSID, WIFI_PASS);
  while (WiFi.status() != WL_CONNECTED) {
    Serial.print('.');
    delay(500);
  }
  Serial.println();
  logLine(String("WiFi OK. IP: ") + WiFi.localIP().toString() + " RSSI: " + String(WiFi.RSSI()));
}

void callback(char* topic, byte* payload, unsigned int length) {
  String message;
  for (unsigned int i = 0; i < length; i++) message += (char)payload[i];
  logLine(String("MQTT RX topic=") + topic + " payload=" + message);

  StaticJsonDocument<256> doc;
  if (deserializeJson(doc, message)) return;

  String command = doc["command"] | "";
  if (command == "on" || command == "led_on") setLed(true);
  else if (command == "off" || command == "led_off") setLed(false);
  else if (command == "toggle" || command == "led_toggle") setLed(!ledState);
  else if (command == "set_led") setLed(doc["params"]["state"] | false);
  else if (command == "set_output") {
    String outName = doc["params"]["name"] | "";
    bool outState = doc["params"]["state"] | false;
    if (setOutputByName(outName, outState)) {
      logLine(String("Output ") + outName + " -> " + (outState ? "ON" : "OFF"));
      publishState();
    }
  }
  else if (command == "set_interval") {
    int sec = doc["params"]["seconds"] | 0;
    if (sec >= 3 && sec <= 3600) {
      telemetryIntervalMs = (unsigned long)sec * 1000UL;
      logLine(String("Telemetry interval -> ") + sec + "s");
      publishState();
    }
  }
  else if (command == "get_state" || command == "ping") {
    publishState();
  }
  else if (command == "reboot") {
    logLine("Remote reboot requested");
    publishState("restarting");
    delay(400);
    ESP.restart();
  }
  else if (command == "ota_update") {
    String firmwareUrl = doc["params"]["url"] | "";
    performOta(firmwareUrl);
  }
}

void connectMQTT() {
  mqtt.setServer(MQTT_HOST, MQTT_PORT);
  mqtt.setCallback(callback);
  mqtt.setKeepAlive(30);
  mqtt.setSocketTimeout(10);
  mqtt.setBufferSize(512);
  logLine(String("MQTT target ") + MQTT_HOST + ":" + String(MQTT_PORT));

  while (!mqtt.connected()) {
    String clientId = buildClientId();
    logLine(String("MQTT connecting. clientId=") + clientId + " user=" + MQTT_USER);
    if (mqtt.connect(clientId.c_str(), MQTT_USER, MQTT_PASS, statusTopic.c_str(), 1, true, R"({"status":"offline"})")) {
      mqtt.subscribe(controlTopic.c_str());
      reconnectDelayMs = 2000;
      logLine(String("MQTT OK. Subscribed: ") + controlTopic);
      publishState("online");
    } else {
      logLine(String("MQTT fail rc=") + mqtt.state() + ", retry in " + reconnectDelayMs + "ms");
      delay(reconnectDelayMs);
      reconnectDelayMs = min(reconnectDelayMs * 2, 30000UL);
    }
  }
}

void setup() {
  Serial.begin(115200);
  delay(200);
  logLine("Booting firmware...");

  for (int i = 0; i < OUTPUT_COUNT; i++) {
    pinMode(outputs[i].pin, OUTPUT);
    outputs[i].state = false;
    bool physical = outputs[i].inverted ? true : false;
    digitalWrite(outputs[i].pin, physical ? HIGH : LOW);
  }

  for (int i = 0; i < INPUT_COUNT; i++) {
    if (inputs[i].analog) pinMode(inputs[i].pin, INPUT);
    else pinMode(inputs[i].pin, inputs[i].pullup ? INPUT_PULLUP : INPUT);
  }

${withAhtBmp ? `  Wire.begin(${cfg.i2cSda}, ${cfg.i2cScl});
  ahtOk = aht.begin();
  ${cfg.bmpAddress === '0x76' ? 'bmpOk = bmp.begin(0x76);' : cfg.bmpAddress === '0x77' ? 'bmpOk = bmp.begin(0x77);' : 'bmpOk = bmp.begin(0x76) || bmp.begin(0x77);'}
  logLine(String("Sensors AHT=") + (ahtOk ? "OK" : "FAIL") + " BMP=" + (bmpOk ? "OK" : "FAIL"));
` : ''}

  logLine(String("Control topic: ") + controlTopic);
  logLine(String("Telemetry topic: ") + telemetryTopic);
  connectWiFi();
  connectMQTT();
}

void loop() {
  if (WiFi.status() != WL_CONNECTED) {
    logLine("WiFi lost, reconnecting...");
    connectWiFi();
  }
  if (!mqtt.connected()) {
    logLine("MQTT disconnected, reconnecting...");
    connectMQTT();
  }
  mqtt.loop();

${withAhtBmp ? '  readSensors();\n' : ''}  if (millis() - lastTelemetry > telemetryIntervalMs) {
    lastTelemetry = millis();
    publishState();
  }
}
`;
  }, [espConfig, ioChannels]);

  React.useEffect(() => {
    loadDevices();
  }, []);

  const loadDevices = async () => {
    try {
      setLoading(true);
      const res = await axios.get('/api/devices', { headers: authHeader() });
      setDevices(res.data.devices || []);
    } catch {
      message.error('Error cargando dispositivos');
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    const timer = setInterval(() => setNowTs(Date.now()), 10000);
    return () => clearInterval(timer);
  }, []);

  React.useEffect(() => {
    const poll = setInterval(() => {
      loadDevices();
    }, 15000);
    return () => clearInterval(poll);
  }, []);

  const getConnectionState = (lastSeen, lastStatus) => {
    if (String(lastStatus || '').toLowerCase() === 'offline') {
      return { online: false, ageSec: null };
    }
    if (!lastSeen) return { online: false, ageSec: null };
    const ts = parseDeviceTimestampMs(lastSeen);
    if (!Number.isFinite(ts)) return { online: false, ageSec: null };
    const ageSec = Math.max(0, Math.floor((nowTs - ts) / 1000));
    return { online: ageSec <= 120, ageSec };
  };

  const handleAddDevice = () => {
    setEditingDevice(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEditDevice = (device) => {
    if (device.access_type === 'shared') { message.warning('No puedes editar dispositivos compartidos'); return; }
    setEditingDevice(device);
    form.setFieldsValue(device);
    setModalVisible(true);
  };

  const handleDeleteDevice = (device) => {
    if (device.access_type === 'shared') {
      Modal.confirm({
        title: '⬅️ Abandonar dispositivo compartido',
        content: `¿Estás seguro que quieres dejar de tener acceso a "${device.name}"? Perderás acceso pero el dispositivo seguirá activo para el propietario.`,
        okText: 'Sí, salir', okType: 'danger', cancelText: 'Cancelar',
        maskClosable: false,
        onOk: async () => {
          try {
            message.loading({ content: 'Removiendo acceso...', key: 'delete' });
            await axios.delete('/api/shares/leave', { headers: authHeader(), data: { device_id: device.device_id, owner_id: device.owner_id } });
            message.success({ content: '✓ Acceso removido exitosamente', key: 'delete', duration: 2 });
            await loadDevices();
          } catch (err) { 
            const errorMsg = err?.response?.data?.error || err?.message || 'Error desconocido al remover acceso';
            message.error({ content: `✗ ${errorMsg}`, key: 'delete', duration: 3 });
          }
        }
      });
      return;
    }
    Modal.confirm({
      title: '🗑️ Confirmar eliminación de dispositivo',
      content: (
        <div style={{ padding: '10px 0' }}>
          <p>¿Estás seguro que deseas eliminar "<strong style={{ color: '#ff4d4f' }}>{device.name}</strong>"?</p>
          <p style={{ color: '#666', fontSize: 12, marginTop: 8 }}>⚠️ Esta acción es irreversible. Se eliminarán:</p>
          <ul style={{ color: '#666', fontSize: 12, marginTop: 4 }}>
            <li>Todos los datos del dispositivo</li>
            <li>Historial de telemetría</li>
            <li>Configuración almacenada</li>
          </ul>
        </div>
      ),
      okText: 'Eliminar dispositivo', okType: 'danger', cancelText: 'Cancelar',
      maskClosable: false,
      onOk: async () => {
        try {
          message.loading({ content: 'Eliminando dispositivo...', key: 'delete' });
          const response = await axios.delete(`/api/devices/${device.device_id}`, { headers: authHeader() });
          message.success({ content: '✓ Dispositivo eliminado exitosamente', key: 'delete', duration: 2 });
          await loadDevices();
        } catch (err) {
          console.error('Delete error:', err);
          let errorMsg = 'Error al eliminar el dispositivo';
          
          if (err?.response?.status === 404) {
            errorMsg = 'El dispositivo no existe o ya fue eliminado';
          } else if (err?.response?.status === 403) {
            errorMsg = 'No tienes permisos para eliminar este dispositivo';
          } else if (err?.response?.status === 409) {
            errorMsg = 'El dispositivo está siendo utilizado y no puede eliminarse';
          } else if (err?.response?.data?.error) {
            errorMsg = err.response.data.error;
          } else if (err?.message) {
            errorMsg = err.message;
          }
          
          message.error({ content: `✗ ${errorMsg}`, key: 'delete', duration: 4 });
        }
      }
    });
  };

  const handleSaveDevice = async (values) => {
    try {
      const payload = { ...values, hardware_config: JSON.stringify(buildHardwareConfigPayload()) };
      if (editingDevice) {
        await axios.put(`/api/devices/${editingDevice.device_id}`, payload, { headers: authHeader() });
        message.success('Actualizado');
      } else {
        await axios.post('/api/devices', payload, { headers: authHeader() });
        message.success('Dispositivo registrado');
      }
      loadDevices(); setModalVisible(false);
    } catch (err) { message.error(err.response?.data?.error || 'Error al guardar'); }
  };

  const handleShare = (device) => { setSharingDevice(device); setShareResult(null); shareForm.resetFields(); setShareModalVisible(true); };

  const handleCreateShare = async (values) => {
    try {
      setShareLoading(true);
      const res = await axios.post('/api/shares/create', { device_id: sharingDevice.device_id, ...values }, { headers: authHeader() });
      setShareResult(res.data);
    } catch (err) { message.error(err.response?.data?.error || 'Error al crear código'); }
    finally { setShareLoading(false); }
  };

  const handleJoin = async (values) => {
    try {
      setJoinLoading(true);
      const res = await axios.post('/api/shares/join', { share_code: values.share_code.trim().toUpperCase(), pin: values.pin }, { headers: authHeader() });
      message.success(`Acceso concedido a "${res.data.device_name}"`);
      setJoinModalVisible(false); joinForm.resetFields(); loadDevices();
    } catch (err) { message.error(err.response?.data?.error || 'Código inválido o expirado'); }
    finally { setJoinLoading(false); }
  };

  const handleOpenOta = (device) => {
    setOtaTargetDevice(device);
    otaForm.setFieldsValue({
      firmware_url: '',
      version: '',
      checksum: '',
      force: false
    });
    setOtaModalVisible(true);
  };

  const handleSendOta = async (values) => {
    if (!otaTargetDevice) return;
    try {
      setOtaLoading(true);
      await axios.post(`/api/devices/${otaTargetDevice.device_id}/ota`, values, { headers: authHeader() });
      message.success('Comando OTA enviado. El dispositivo iniciará actualización cuando reciba el comando.');
      setOtaModalVisible(false);
    } catch (err) {
      message.error(err.response?.data?.error || 'Error enviando OTA');
    } finally {
      setOtaLoading(false);
    }
  };

  const handleCopyGeneratedCode = async () => {
    try {
      await navigator.clipboard.writeText(generatedEsp32Code);
      message.success('Código ESP32 copiado al portapapeles');
    } catch {
      message.error('No se pudo copiar automáticamente');
    }
  };

  const handleDownloadGeneratedCode = () => {
    const safeDeviceId = (espConfig.deviceId || 'esp32-device').replace(/[^a-zA-Z0-9_-]/g, '_');
    const blob = new Blob([generatedEsp32Code], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${safeDeviceId}.ino`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const buildHardwareConfigPayload = React.useCallback(() => ({
    projectName,
    useCase,
    ioChannels: ioChannels.map((c) => ({
      name: c.name,
      direction: c.direction,
      signal: c.signal,
      pin: Number(c.pin) || 0,
      inverted: !!c.inverted,
      pullup: !!c.pullup,
    })),
    network: {
      telemetrySeconds: Number(espConfig.telemetrySeconds) || 10,
      mqttHost: espConfig.mqttHost,
      mqttPort: Number(espConfig.mqttPort) || 1883,
    },
    sensors: {
      mode: espConfig.sensorMode || 'none',
      i2cSda: Number(espConfig.i2cSda) || 21,
      i2cScl: Number(espConfig.i2cScl) || 22,
      bmpAddress: espConfig.bmpAddress || 'auto',
    }
  }), [projectName, useCase, ioChannels, espConfig.telemetrySeconds, espConfig.mqttHost, espConfig.mqttPort, espConfig.sensorMode, espConfig.i2cSda, espConfig.i2cScl, espConfig.bmpAddress]);

  const handleCreateAndDownload = async () => {
    try {
      const values = await form.validateFields();
      const payload = { ...values, hardware_config: JSON.stringify(buildHardwareConfigPayload()) };
      try {
        await axios.post('/api/devices', payload, { headers: authHeader() });
        message.success('Dispositivo creado automáticamente');
        loadDevices();
      } catch (err) {
        if (err.response?.status === 409) {
          message.info('El dispositivo ya existía, se usará igualmente');
        } else {
          throw err;
        }
      }
      handleDownloadGeneratedCode();
    } catch (err) {
      if (err?.errorFields) {
        message.warning('Completa los campos requeridos para registrar el dispositivo');
      } else {
        message.error(err.response?.data?.error || 'No se pudo crear el dispositivo automáticamente');
      }
    }
  };

  const wizardItems = [
    { title: 'Objetivo' },
    { title: 'Conexion' },
    { title: 'Hardware' },
    { title: 'Generar' },
  ];

  const goWizardNext = () => {
    if (wizardStep === 1) {
      if (!espConfig.wifiSsid || !espConfig.wifiPassword) {
        message.warning('Completa SSID y clave WiFi antes de continuar');
        return;
      }
      if (!espConfig.mqttHost || !espConfig.mqttUser || !espConfig.mqttPassword) {
        message.warning('Completa la configuracion MQTT antes de continuar');
        return;
      }
    }

    if (wizardStep === 2) {
      if (!ioChannels.length) {
        message.warning('Agrega al menos un canal de entrada o salida');
        return;
      }
      const invalid = ioChannels.find((c) => !c.name || Number(c.pin) < 0);
      if (invalid) {
        message.warning('Revisa hardware: todos los canales deben tener nombre y pin valido');
        return;
      }
    }

    setWizardStep((s) => Math.min(3, s + 1));
  };

  const useCaseCards = Object.entries(USE_CASE_TEMPLATES).map(([key, info]) => ({ key, ...info }));

  const columns = [
    {
      title: 'Nombre', dataIndex: 'name', key: 'name',
      render: (text, r) => (
        <Space direction="vertical" size={0}>
          <strong>{text}</strong>
          {r.access_type === 'shared' && <Tag color="purple" style={{ fontSize: 10 }}>Compartido por {r.owner_username}</Tag>}
        </Space>
      )
    },
    { title: 'Device ID', dataIndex: 'device_id', key: 'device_id', render: (t) => <code style={{ fontSize: 12 }}>{t}</code> },
    { title: 'Tipo', dataIndex: 'type', key: 'type', render: (t) => <Tag color="blue">{t || 'generic'}</Tag> },
    { title: 'Ubicación', dataIndex: 'location', key: 'location' },
    {
      title: 'Estado', dataIndex: 'last_seen', key: 'status',
      render: (v, r) => {
        const state = getConnectionState(v, r.last_status);
        return state.online
          ? <Tag color="green">En línea</Tag>
          : <Tag color="red">Offline</Tag>;
      }
    },
    {
      title: 'Telemetría', key: 'telemetry',
      render: (_, r) => {
        const hasAny = r.latest_temp != null || r.latest_hum != null || r.latest_pres != null;
        if (!hasAny) return <Text type="secondary">Sin datos</Text>;
        return (
          <Space direction="vertical" size={0}>
            <Text>🌡️ {r.latest_temp != null ? `${Number(r.latest_temp).toFixed(1)} °C` : '—'}</Text>
            <Text>💧 {r.latest_hum != null ? `${Number(r.latest_hum).toFixed(0)} %` : '—'}</Text>
            <Text>🧭 {r.latest_pres != null ? `${Number(r.latest_pres).toFixed(0)} hPa` : '—'}</Text>
          </Space>
        );
      }
    },
    {
      title: 'Última señal', dataIndex: 'last_seen', key: 'last_seen',
      render: (v, r) => {
        const state = getConnectionState(v, r.last_status);
        if (!v) return 'Nunca';
        const ts = parseDeviceTimestampMs(v);
        const when = Number.isFinite(ts) ? new Date(ts).toLocaleString('es-CL') : v;
        if (state.ageSec == null) return when;
        return `${when} (${state.ageSec}s)`;
      }
    },
    {
      title: 'Acceso', dataIndex: 'access_type', key: 'access_type',
      render: (v, r) => v === 'shared' ? <Tag color="purple">{r.share_permissions || 'read'}</Tag> : <Tag color="green">Propietario</Tag>
    },
    {
      title: 'Acciones', key: 'actions',
      render: (_, record) => (
        <Space>
          {record.access_type === 'owner' && (
            <>
              <Button size="small" icon={<EditOutlined />} onClick={() => handleEditDevice(record)}>Editar</Button>
              <Button size="small" icon={<ShareAltOutlined />} onClick={() => handleShare(record)}>Compartir</Button>
              <Button size="small" onClick={() => handleOpenOta(record)}>OTA</Button>
            </>
          )}
          <Button size="small" danger icon={<DeleteOutlined />} onClick={() => handleDeleteDevice(record)}>
            {record.access_type === 'shared' ? 'Salir' : 'Eliminar'}
          </Button>
        </Space>
      )
    },
  ];

  return (
    <div style={{ 
      padding: '24px', 
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
    }}>
      {/* Header */}
      <div style={{ marginBottom: 32, color: 'white' }}>
        <h1 style={{ fontSize: 40, fontWeight: 800, margin: '0 0 8px 0', textShadow: '0 2px 4px rgba(0,0,0,0.2)' }}>
          ⚡ Gestión de Dispositivos ESP32
        </h1>
        <p style={{ fontSize: 16, margin: 0, opacity: 0.9 }}>Controla, configura y monitorea todos tus dispositivos IoT en un solo lugar</p>
      </div>

      {/* Action Buttons */}
      <div style={{ marginBottom: 24 }}>
        <Space size={12} wrap>
          <Button 
            type="primary" 
            size="large"
            icon={<PlusOutlined />} 
            onClick={handleAddDevice}
            style={{ 
              fontSize: 15, 
              height: 44, 
              paddingInline: 24,
              background: '#ff6b6b',
              border: 'none',
              fontWeight: 600,
              boxShadow: '0 4px 15px rgba(255, 107, 107, 0.4)',
              color: 'white'
            }}
          >
            ➕ Nuevo Dispositivo
          </Button>
          <Button 
            size="large"
            icon={<UserAddOutlined />} 
            onClick={() => setJoinModalVisible(true)}
            style={{ 
              fontSize: 15, 
              height: 44, 
              paddingInline: 24,
              background: '#4ecdc4',
              border: 'none',
              fontWeight: 600,
              color: 'white',
              boxShadow: '0 4px 15px rgba(78, 205, 196, 0.4)'
            }}
          >
            🔗 Unirse
          </Button>
          <Button 
            size="large"
            icon={<ReloadOutlined />} 
            onClick={loadDevices} 
            loading={loading}
            style={{ 
              fontSize: 15, 
              height: 44, 
              paddingInline: 24,
              background: '#45b7d1',
              border: 'none',
              fontWeight: 600,
              color: 'white',
              boxShadow: '0 4px 15px rgba(69, 183, 209, 0.4)'
            }}
          >
            🔄 Actualizar
          </Button>
        </Space>
      </div>

      {/* Main Content Card */}
      <Card
        style={{
          marginBottom: 18,
          borderRadius: 14,
          border: '1px solid #1f2937',
          background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
          boxShadow: '0 8px 28px rgba(2, 6, 23, 0.32)',
        }}
        bodyStyle={{ padding: 20 }}
      >
        <Space direction="vertical" size={14} style={{ width: '100%' }}>
          <div style={{ color: '#f8fafc' }}>
            <div style={{ fontSize: 20, fontWeight: 800, letterSpacing: 0.5 }}>
              BANCO DE CODIGOS Y ARQUITECTURAS DE CONEXION
            </div>
            <div style={{ fontSize: 13, opacity: 0.88 }}>
              Sección nueva para guardar tus archivos. No incluye buscador.
            </div>
          </div>

          <Upload.Dragger
            multiple
            showUploadList={false}
            beforeUpload={(file) => {
              addCodeBankFile(file);
              message.success(`Archivo agregado: ${file.name}`);
              return false;
            }}
            style={{ background: 'rgba(255,255,255,0.95)', borderRadius: 12 }}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined style={{ color: '#2563eb' }} />
            </p>
            <p className="ant-upload-text">Arrastra archivos aquí o haz clic para agregarlos</p>
            <p className="ant-upload-hint">Quedan guardados como inventario local de referencia.</p>
          </Upload.Dragger>

          <Card size="small" style={{ borderRadius: 10, background: '#f8fafc' }}>
            <List
              locale={{ emptyText: 'Aún no hay archivos guardados en el banco.' }}
              dataSource={codeBankFiles}
              renderItem={(item) => (
                <List.Item
                  actions={[
                    <Button key="remove" size="small" danger onClick={() => removeCodeBankFile(item.id)}>
                      Quitar
                    </Button>
                  ]}
                >
                  <List.Item.Meta
                    avatar={<FileTextOutlined style={{ fontSize: 18, color: '#334155' }} />}
                    title={<span style={{ fontWeight: 600 }}>{item.name}</span>}
                    description={`${formatBytes(item.size)} · ${new Date(item.addedAt).toLocaleString('es-CL')}`}
                  />
                </List.Item>
              )}
            />
          </Card>
        </Space>
      </Card>

      <Card 
        style={{ 
          borderRadius: 16, 
          boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)',
          border: 'none',
          backgroundColor: '#ffffff'
        }}
        bodyStyle={{ padding: 32 }}
      >
        {isMobile ? (
          // Mobile View: Card Grid
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            {devices.length > 0 ? (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 16 }}>
                  {devices.map((record) => {
                    const state = getConnectionState(record.last_seen, record.last_status);
                    const lastSeenText = record.last_seen
                      ? new Date(parseDeviceTimestampMs(record.last_seen)).toLocaleString('es-CL')
                      : 'Nunca';
                    return (
                      <Card
                        key={`${record.owner_id}-${record.device_id}`}
                        style={{
                          borderRadius: 12,
                          border: state.online ? '2px solid #4ecdc4' : '2px solid #ddd',
                          background: state.online 
                            ? 'linear-gradient(135deg, rgba(78, 205, 196, 0.05), rgba(245, 176, 65, 0.05))'
                            : '#fafafa',
                          boxShadow: state.online ? '0 4px 12px rgba(78, 205, 196, 0.2)' : '0 2px 8px rgba(0,0,0,0.05)'
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: 16 }}>
                          <div>
                            <h3 style={{ margin: '0 0 4px 0', fontSize: 18, fontWeight: 700, color: '#1f2937' }}>
                              📱 {record.name}
                            </h3>
                            <Tag color={state.online ? 'green' : 'red'} style={{ marginRight: 0 }}>
                              {state.online ? '● En línea' : '● Offline'}
                            </Tag>
                          </div>
                          <Tag color={record.access_type === 'shared' ? 'purple' : 'blue'}>
                            {record.access_type === 'shared' ? '👥 Compartido' : '👤 Propietario'}
                          </Tag>
                        </div>
                        <Divider style={{ margin: '12px 0' }} />
                        <Space direction="vertical" size={8} style={{ width: '100%' }}>
                          <div><Text type="secondary" style={{ fontSize: 12 }}>Device ID:</Text> <code style={{ fontSize: 11 }}>{record.device_id}</code></div>
                          <div><Text type="secondary" style={{ fontSize: 12 }}>Tipo:</Text> <Tag color="blue">{record.type || 'generic'}</Tag></div>
                          <div><Text type="secondary" style={{ fontSize: 12 }}>Ubicación:</Text> <Text>{record.location || '—'}</Text></div>
                          <div>
                            <Text type="secondary" style={{ fontSize: 12 }}>Telemetría:</Text>{' '}
                            <Text>
                              🌡️ {record.latest_temp != null ? `${Number(record.latest_temp).toFixed(1)} °C` : '—'} ·{' '}
                              💧 {record.latest_hum != null ? `${Number(record.latest_hum).toFixed(0)} %` : '—'} ·{' '}
                              🧭 {record.latest_pres != null ? `${Number(record.latest_pres).toFixed(0)} hPa` : '—'}
                            </Text>
                          </div>
                          <div><Text type="secondary" style={{ fontSize: 12 }}>Última señal:</Text> <Text>{lastSeenText}</Text></div>
                          <Divider style={{ margin: '8px 0' }} />
                          <Space direction="vertical" style={{ width: '100%' }} size={6}>
                            {record.access_type === 'owner' && (
                              <>
                                <Button size="small" block icon={<EditOutlined />} onClick={() => handleEditDevice(record)} style={{ borderRadius: 6 }}>
                                  ✏️ Editar
                                </Button>
                                <Button size="small" block icon={<ShareAltOutlined />} onClick={() => handleShare(record)} style={{ borderRadius: 6 }}>
                                  🔗 Compartir
                                </Button>
                                <Button size="small" block onClick={() => handleOpenOta(record)} style={{ borderRadius: 6 }}>
                                  📦 OTA
                                </Button>
                              </>
                            )}
                            <Button 
                              size="small" 
                              block 
                              danger 
                              onClick={() => handleDeleteDevice(record)}
                              style={{ borderRadius: 6 }}
                            >
                              🗑️ {record.access_type === 'shared' ? 'Abandonar' : 'Eliminar'}
                            </Button>
                          </Space>
                        </Space>
                      </Card>
                    );
                  })}
                </div>
              </>
            ) : (
              <Empty description="No hay dispositivos" style={{ paddingTop: 40 }} />
            )}
          </Space>
        ) : (
          // Desktop View: Enhanced Table with Better Styling
          <Table
            dataSource={devices}
            columns={columns}
            rowKey={(r) => `${r.owner_id}-${r.device_id}`}
            loading={loading}
            pagination={{ pageSize: 10, showSizeChanger: true, showTotal: (total) => `Total: ${total} dispositivo(s)` }}
            scroll={{ x: 1200 }}
            style={{ fontSize: 13 }}
            rowClassName={(record) => {
              const st = getConnectionState(record.last_seen, record.last_status);
              return st.online ? 'online-row' : 'offline-row';
            }}
            expandable={{
              expandedRowRender: (record) => (
                <Collapse
                  size="small"
                  items={[
                    {
                      key: 'pinmap',
                      label: '📍 Mapa de pines ESP32',
                      children: <Esp32PinMap hardwareConfig={record.hardware_config} fallbackChannels={[{ name: 'led', direction: 'output', signal: 'digital', pin: 2 }]} />
                    }
                  ]}
                />
              ),
              rowExpandable: () => true,
            }}
          />
        )}
      </Card>

      {/* Modals and Forms */}

      {/* Global Styles */}
      <style>{`
        .offline-row {
          opacity: 0.6;
          background-color: #f5f5f5 !important;
        }
        
        .online-row {
          background-color: #f0fdf4 !important;
        }
        
        .ant-modal-header {
          border-bottom: 2px solid #f0f0f0;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        }
        
        .ant-modal-title {
          font-size: 20px;
          font-weight: 700;
          color: white;
        }
        
        .ant-form-item-label > label {
          font-weight: 600;
          color: #374151;
        }
        
        .ant-input, .ant-input-number, .ant-select {
          border-radius: 8px !important;
          border: 1px solid #e5e7eb !important;
          transition: all 0.3s ease !important;
        }
        
        .ant-input:hover, .ant-input-number:hover, .ant-select:hover {
          border-color: #667eea !important;
        }
        
        .ant-input:focus, .ant-input-number:focus, .ant-select:focus {
          border-color: #667eea !important;
          box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1) !important;
        }
        
        .ant-btn {
          border-radius: 8px;
          font-weight: 600;
          transition: all 0.3s ease;
          border: none;
        }
        
        .ant-btn-primary {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        }
        
        .ant-btn:not(.ant-btn-text):hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
        }
        
        .ant-card {
          transition: all 0.3s ease;
          border-radius: 12px;
        }
        
        .ant-card:hover {
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
          transform: translateY(-2px);
        }
        
        .ant-tag {
          border-radius: 6px;
          padding: 4px 12px;
          font-weight: 500;
        }
        
        .ant-table {
          font-size: 13px;
        }
        
        .ant-table-row:hover {
          background-color: rgba(102, 126, 234, 0.05) !important;
        }
      `}</style>

      <Modal 
        title={editingDevice ? '✏️ Editar Dispositivo' : '✨ Crear Nuevo Dispositivo'} 
        open={modalVisible}
        onOk={() => form.submit()} 
        onCancel={() => setModalVisible(false)} 
        okText={editingDevice ? 'Actualizar' : 'Guardar'}
        cancelText="Cancelar"
        width={1000}
        style={{ top: 20 }}
        bodyStyle={{ maxHeight: '75vh', overflowY: 'auto', padding: 24 }}
      >
        {!editingDevice && (
          <>
            <Alert
              type="info"
              showIcon
              icon={<span style={{ fontSize: 16 }}>ℹ️</span>}
              style={{ marginBottom: 20, borderRadius: 6 }}
              message="Guía de conexión remota"
              description="Usa el mismo Device ID en la app y en el ESP32. Compatible con domotica.nc3civil.cl:1883 para acceso remoto global. En producción recomendamos TLS en puerto 8883."
            />
            <Collapse
              size="large"
              items={[
                {
                  key: 'steps',
                  label: '📚 Instrucciones paso a paso',
                  children: (
                    <ol style={{ margin: 0, paddingLeft: 20, lineHeight: 1.8, color: '#4b5563' }}>
                      <li><strong>Instala librerías:</strong> En Arduino IDE: Biblioteca → Administrador de Bibliotecas → busca "PubSubClient" y "ArduinoJson" → instala</li>
                      <li><strong>Configura WiFi:</strong> Tu red WiFi (SSID) y contraseña para que el ESP32 se conecte</li>
                      <li><strong>ID único del dispositivo:</strong> Ej: "ESP32-SALON-01". Úsalo en la app y en el sketch</li>
                      <li><strong>Credenciales MQTT:</strong> Usuario y contraseña para autenticarse en el broker</li>
                      <li><strong>Acceso remoto:</strong> El ESP32 se conecta a <code>domotica.nc3civil.cl:1883</code> desde cualquier lugar</li>
                      <li><strong>Temas MQTT:</strong> Publica en <code>esp32/[DEVICE_ID]/telemetry</code> y escucha en <code>esp32/[DEVICE_ID]/control</code></li>
                      <li><strong>Formato JSON:</strong> Envía compacto como <code>{"{"}led:1, rssi:-61, uptime:1234{"}"}</code></li>
                      <li><strong>Seguridad:</strong> En producción final, no dejes secretos en el código. Usa aprovisionamiento o QR.</li>
                    </ol>
                  )
                },
                {
                  key: 'generator',
                  label: '🤖 Generador de código ESP32 (no-code)',
                  children: (
                    <Space direction="vertical" style={{ width: '100%' }} size={16}>
                      <div style={{ backgroundColor: '#f0f9ff', padding: 12, borderRadius: 6, borderLeft: '4px solid #3b82f6' }}>
                        <Text>Responde los pasos y obtén un firmware <strong>listo para compilar y cargar</strong> en tu ESP32</Text>
                      </div>
                      
                      <Steps size="small" current={wizardStep} items={wizardItems} style={{ backgroundColor: '#f5f7fa', padding: 16, borderRadius: 6 }} />

                      {wizardStep === 0 && (
                        <Space direction="vertical" style={{ width: '100%' }} size={14}>
                          <div>
                            <Text strong style={{ display: 'block', marginBottom: 8 }}>Nombre del proyecto</Text>
                            <Input
                              value={projectName}
                              onChange={(e) => setProjectName(e.target.value)}
                              placeholder="Ej: Casa principal, Oficina, Invernadero"
                              size="large"
                              allowClear
                            />
                          </div>
                          
                          <div>
                            <Text strong style={{ display: 'block', marginBottom: 12, fontSize: 15 }}>¿Para qué vas a usar este ESP32?</Text>
                            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 12 }}>
                              {useCaseCards.map((u) => (
                                <Card 
                                  key={u.key} 
                                  size="small" 
                                  hoverable 
                                  onClick={() => applyUseCaseTemplate(u.key)} 
                                  style={{ 
                                    borderColor: useCase === u.key ? '#1677ff' : '#d9d9d9',
                                    borderWidth: useCase === u.key ? 2 : 1,
                                    backgroundColor: useCase === u.key ? '#f0f7ff' : 'white',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s'
                                  }}
                                  bodyStyle={{ padding: 12 }}
                                >
                                  <Space direction="vertical" size={2}>
                                    <Text strong style={{ fontSize: 14 }}>{u.label}</Text>
                                    <Text type="secondary" style={{ fontSize: 12 }}>{u.description}</Text>
                                  </Space>
                                </Card>
                              ))}
                            </div>
                          </div>
                        </Space>
                      )}

                      {wizardStep === 1 && (
                        <Space direction="vertical" style={{ width: '100%' }} size={12}>
                          <div style={{ backgroundColor: '#fef3c7', padding: 12, borderRadius: 6, borderLeft: '4px solid #f59e0b' }}>
                            <Text style={{ fontSize: 12 }}>📡 Configura cómo tu ESP32 se conectará a Internet y al servidor MQTT</Text>
                          </div>
                          
                          <Divider style={{ margin: '8px 0' }} />
                          
                          <Text strong style={{ fontSize: 14 }}>🔗 Conectividad WiFi</Text>
                          <Input 
                            value={espConfig.wifiSsid} 
                            onChange={(e) => setEspConfig((p) => ({ ...p, wifiSsid: e.target.value }))} 
                            placeholder="Nombre de red WiFi (SSID)" 
                            allowClear
                          />
                          <Input.Password 
                            value={espConfig.wifiPassword} 
                            onChange={(e) => setEspConfig((p) => ({ ...p, wifiPassword: e.target.value }))} 
                            placeholder="Clave WiFi" 
                          />
                          <Input 
                            value={espConfig.deviceId} 
                            onChange={(e) => setEspConfig((p) => ({ ...p, deviceId: e.target.value }))} 
                            placeholder="ID del dispositivo (ej: ESP32-SALON-01)" 
                            allowClear
                          />
                          
                          <Divider style={{ margin: '12px 0' }} />
                          
                          <Text strong style={{ fontSize: 14 }}>🌐 Broker MQTT</Text>
                          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '2fr 1fr', gap: 12 }}>
                            <Input 
                              value={espConfig.mqttHost} 
                              onChange={(e) => setEspConfig((p) => ({ ...p, mqttHost: e.target.value }))} 
                              placeholder="Host MQTT (ej: domotica.nc3civil.cl)" 
                              allowClear
                            />
                            <InputNumber 
                              style={{ width: '100%' }} 
                              min={1} 
                              max={65535} 
                              value={espConfig.mqttPort} 
                              onChange={(v) => setEspConfig((p) => ({ ...p, mqttPort: v || 1883 }))} 
                              placeholder="Puerto"
                            />
                          </div>
                          <Input 
                            value={espConfig.mqttUser} 
                            onChange={(e) => setEspConfig((p) => ({ ...p, mqttUser: e.target.value }))} 
                            placeholder="Usuario MQTT" 
                            allowClear
                          />
                          <Input.Password 
                            value={espConfig.mqttPassword} 
                            onChange={(e) => setEspConfig((p) => ({ ...p, mqttPassword: e.target.value }))} 
                            placeholder="Password MQTT" 
                          />
                          
                          <Divider style={{ margin: '12px 0' }} />
                          
                          <Text strong style={{ fontSize: 14 }}>⏱️ Telemetría</Text>
                          <InputNumber 
                            style={{ width: '100%' }} 
                            min={3} 
                            max={3600} 
                            value={espConfig.telemetrySeconds} 
                            onChange={(v) => setEspConfig((p) => ({ ...p, telemetrySeconds: v || 10 }))} 
                            addonAfter="segundos"
                            placeholder="Cada cuántos segundos enviar datos"
                          />
                          
                          <Divider style={{ margin: '12px 0' }} />
                          
                          <Text strong style={{ fontSize: 14 }}>🌡️ Sensores I2C integrados</Text>
                          <Select
                            value={espConfig.sensorMode}
                            onChange={(v) => setEspConfig((p) => ({ ...p, sensorMode: v }))}
                            options={[
                              { value: 'none', label: '❌ Sin sensores (solo GPIO)' },
                              { value: 'aht20_bmp280', label: '📊 AHT20 + BMP280 (temperatura, humedad, presión)' },
                            ]}
                            style={{ width: '100%' }}
                          />
                          {espConfig.sensorMode === 'aht20_bmp280' && (
                            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr', gap: 12, width: '100%', backgroundColor: '#f0f9ff', padding: 12, borderRadius: 6 }}>
                              <InputNumber
                                style={{ width: '100%' }}
                                min={0}
                                max={39}
                                value={espConfig.i2cSda}
                                onChange={(v) => setEspConfig((p) => ({ ...p, i2cSda: Number(v) || 21 }))}
                                addonBefore="SDA"
                                placeholder="GPIO SDA"
                              />
                              <InputNumber
                                style={{ width: '100%' }}
                                min={0}
                                max={39}
                                value={espConfig.i2cScl}
                                onChange={(v) => setEspConfig((p) => ({ ...p, i2cScl: Number(v) || 22 }))}
                                addonBefore="SCL"
                                placeholder="GPIO SCL"
                              />
                              <Select
                                value={espConfig.bmpAddress}
                                onChange={(v) => setEspConfig((p) => ({ ...p, bmpAddress: v }))}
                                options={[
                                  { value: 'auto', label: 'Auto (0x76/0x77)' },
                                  { value: '0x76', label: '0x76' },
                                  { value: '0x77', label: '0x77' },
                                ]}
                              />
                            </div>
                          )}
                        </Space>
                      )}

                      {wizardStep === 2 && (
                        <Card size="small" title="⚙️ Entradas y salidas (GPIO)" style={{ width: '100%', borderRadius: 6 }}>
                          <Space direction="vertical" style={{ width: '100%' }} size={12}>
                            <div style={{ backgroundColor: '#e0f2fe', padding: 12, borderRadius: 6, borderLeft: '4px solid #0284c7' }}>
                              <Text type="secondary" style={{ fontSize: 12 }}>Configura cada pin. Ej: Salida relay1 en GPIO 5, Entrada botón en GPIO 4 con pullup</Text>
                            </div>
                            
                            {ioChannels.map((ch, idx) => (
                              <div key={ch.id} style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1.2fr 1fr 1fr 90px 100px 90px', gap: 8, width: '100%', backgroundColor: idx % 2 === 0 ? '#fafafa' : 'white', padding: 12, borderRadius: 4 }}>
                                <Input 
                                  placeholder="Nombre del canal" 
                                  value={ch.name} 
                                  onChange={(e) => setIoChannels((prev) => prev.map((x) => x.id === ch.id ? { ...x, name: e.target.value } : x))}
                                  size="small"
                                />
                                <Select 
                                  value={ch.direction} 
                                  onChange={(v) => setIoChannels((prev) => prev.map((x) => x.id === ch.id ? { ...x, direction: v } : x))} 
                                  options={[{ value: 'output', label: 'Salida' }, { value: 'input', label: 'Entrada' }]}
                                  size="small"
                                />
                                <Select 
                                  value={ch.signal} 
                                  onChange={(v) => setIoChannels((prev) => prev.map((x) => x.id === ch.id ? { ...x, signal: v } : x))} 
                                  options={[{ value: 'digital', label: 'Digital' }, { value: 'analog', label: 'Analógica' }]}
                                  size="small"
                                />
                                <InputNumber 
                                  min={0} 
                                  max={39} 
                                  value={ch.pin} 
                                  onChange={(v) => setIoChannels((prev) => prev.map((x) => x.id === ch.id ? { ...x, pin: v ?? 0 } : x))}
                                  size="small"
                                  placeholder="GPIO"
                                />
                                {ch.direction === 'output'
                                  ? <Switch 
                                      checked={!!ch.inverted} 
                                      onChange={(v) => setIoChannels((prev) => prev.map((x) => x.id === ch.id ? { ...x, inverted: v } : x))} 
                                      checkedChildren="Inv" 
                                      unCheckedChildren="Norm"
                                    />
                                  : <Switch 
                                      checked={!!ch.pullup} 
                                      onChange={(v) => setIoChannels((prev) => prev.map((x) => x.id === ch.id ? { ...x, pullup: v } : x))} 
                                      checkedChildren="PU" 
                                      unCheckedChildren="NoPU"
                                    />
                                }
                                <Button 
                                  danger 
                                  size="small"
                                  icon={<DeleteOutlined />} 
                                  onClick={() => setIoChannels((prev) => prev.filter((x) => x.id !== ch.id))}
                                >
                                  Quitar
                                </Button>
                              </div>
                            ))}
                            
                            <Button 
                              icon={<PlusOutlined />} 
                              onClick={() => setIoChannels((prev) => ([...prev, { id: Date.now(), name: `ch${prev.length + 1}`, direction: 'output', signal: 'digital', pin: 5, inverted: false, pullup: false }]))}
                              block
                              style={{ borderRadius: 6 }}
                            >
                              Agregar nuevo canal
                            </Button>
                          </Space>
                        </Card>
                      )}

                      {wizardStep === 3 && (
                        <Space direction="vertical" style={{ width: '100%' }} size={14}>
                          <Alert 
                            type="success" 
                            showIcon
                            icon={<span style={{ fontSize: 16 }}>✅</span>}
                            message="¡Tu firmware está listo!" 
                            description={`Proyecto: ${projectName} • Perfil: ${USE_CASE_TEMPLATES[useCase]?.label || 'Personalizado'} • Canales: ${ioChannels.length}`}
                            style={{ borderRadius: 6 }}
                          />
                          
                          <div>
                            <Text strong style={{ display: 'block', marginBottom: 8 }}>Código ESP32 (Arduino)</Text>
                            <Input.TextArea 
                              value={generatedEsp32Code} 
                              autoSize={{ minRows: 16, maxRows: 28 }} 
                              readOnly
                              style={{ fontFamily: 'monospace', fontSize: 11, borderRadius: 6 }}
                            />
                          </div>
                          
                          <Space wrap style={{ width: '100%', justifyContent: 'center' }}>
                            <Button icon={<CopyOutlined />} onClick={handleCopyGeneratedCode} style={{ borderRadius: 6 }}>
                              📋 Copiar código
                            </Button>
                            <Button type="primary" icon={<DownloadOutlined />} onClick={handleDownloadGeneratedCode} style={{ borderRadius: 6 }}>
                              ⬇️ Descargar .ino
                            </Button>
                            <Button type="primary" onClick={handleCreateAndDownload} style={{ borderRadius: 6 }}>
                              ⚡ Registrar + Descargar
                            </Button>
                          </Space>
                        </Space>
                      )}

                      <Space style={{ justifyContent: 'space-between', width: '100%' }}>
                        <Button disabled={wizardStep === 0} onClick={() => setWizardStep((s) => Math.max(0, s - 1))} style={{ borderRadius: 6 }}>
                          ← Anterior
                        </Button>
                        <span style={{ color: '#6b7280', fontSize: 12 }}>Paso {wizardStep + 1} de {wizardItems.length}</span>
                        <Button type="primary" disabled={wizardStep === 3} onClick={goWizardNext} style={{ borderRadius: 6 }}>
                          Siguiente →
                        </Button>
                      </Space>
                    </Space>
                  )
                },
                {
                  key: 'standard',
                  label: '🔒 Estándar recomendado para producción',
                  children: (
                    <ul style={{ margin: 0, paddingLeft: 20, lineHeight: 1.8, color: '#4b5563' }}>
                      <li>MQTT sobre <strong>TLS en puerto 8883</strong> con certificado del broker</li>
                      <li><strong>Credenciales únicas</strong> por dispositivo, no una contraseña compartida para toda la flota</li>
                      <li><strong>Rotación de secretos</strong> y revocación rápida por dispositivo comprometido</li>
                      <li>Client ID único derivado de Device ID + MAC, con <strong>keepalive</strong> y <strong>last-will</strong></li>
                      <li>QoS 1 para comandos importantes y telemetría crítica</li>
                      <li>Firmware sin secretos en producción final; aprovisionamiento por lote o QR</li>
                    </ul>
                  )
                }
              ]}
              style={{ marginBottom: 12 }}
            />
          </>
        )}
        
        <Divider style={{ margin: '20px 0' }} />
        
        <Form 
          form={form} 
          layout="vertical" 
          onFinish={handleSaveDevice}
          style={{ marginTop: editingDevice ? 0 : 20 }}
        >
          <Form.Item 
            name="device_id" 
            label={<span><strong>Device ID</strong> <span style={{ color: '#6b7280' }}>(identificador único en MQTT)</span></span>}
            rules={[{ required: true, message: 'El Device ID es obligatorio' }]}
          >
            <Input 
              placeholder="ESP32-SALON-01" 
              disabled={!!editingDevice}
              size="large"
            />
          </Form.Item>
          
          <Form.Item 
            name="name" 
            label={<span><strong>Nombre del dispositivo</strong> <span style={{ color: '#6b7280' }}>(para identificarlo en la app)</span></span>}
            rules={[{ required: true, message: 'El nombre es obligatorio' }]}
          >
            <Input 
              placeholder="Ej: Sensor Temperatura Salón" 
              size="large"
            />
          </Form.Item>
          
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 16 }}>
            <Form.Item 
              name="type" 
              label={<strong>Tipo de dispositivo</strong>}
              initialValue="generic"
            >
              <Select placeholder="Selecciona tipo" size="large">
                <Select.Option value="sensor">🌡️ Sensor</Select.Option>
                <Select.Option value="relay">🔌 Relay/Control</Select.Option>
                <Select.Option value="multi">📊 Multi-sensor</Select.Option>
                <Select.Option value="generic">⚙️ Genérico</Select.Option>
              </Select>
            </Form.Item>
            
            <Form.Item 
              name="location" 
              label={<strong>Ubicación</strong>}
            >
              <Input 
                placeholder="Ej: Oficina Principal" 
                size="large"
              />
            </Form.Item>
          </div>
          
          <Form.Item 
            name="mqtt_topic" 
            label={<span><strong>Topic MQTT personalizado</strong> <span style={{ color: '#6b7280' }}>(opcional)</span></span>}
          >
            <Input 
              placeholder="esp32/ESP32-SALON-01/telemetry" 
              size="large"
            />
          </Form.Item>
        </Form>
      </Modal>

      <Modal 
        title={`💾 Compartir dispositivo: "${sharingDevice?.name}"`} 
        open={shareModalVisible}
        onCancel={() => { setShareModalVisible(false); setShareResult(null); }}
        footer={shareResult ? [
          <Button key="c" onClick={() => { setShareModalVisible(false); setShareResult(null); }} style={{ borderRadius: 6 }}>
            Cerrar
          </Button>
        ] : null}
        width={700}
        bodyStyle={{ padding: 24 }}
      >
        {!shareResult ? (
          <Form form={shareForm} layout="vertical" onFinish={handleCreateShare}>
            <Alert
              type="info"
              showIcon
              message="Crea un código para compartir acceso"
              description="El usuario que reciba el código podrá acceder a este dispositivo en modo lectura o lectura+control según lo que configures."
              style={{ marginBottom: 16, borderRadius: 6 }}
            />
            
            <Form.Item 
              name="permissions" 
              label={<strong>Permisos</strong>}
              initialValue="read"
            >
              <Select size="large">
                <Select.Option value="read">👁️ Solo lectura (ver datos)</Select.Option>
                <Select.Option value="write">🎮 Lectura y control (ver + controlar)</Select.Option>
              </Select>
            </Form.Item>
            
            <Form.Item 
              name="max_uses" 
              label={<strong>Usos máximos</strong>}
              initialValue={1}
            >
              <InputNumber 
                min={1} 
                max={100} 
                style={{ width: '100%' }}
                size="large"
                placeholder="Cuántas veces puede usarse el código"
              />
            </Form.Item>
            
            <Form.Item 
              name="expires_hours" 
              label={<strong>Expira en (horas)</strong>}
              initialValue={48}
            >
              <InputNumber 
                min={1} 
                max={8760} 
                style={{ width: '100%' }}
                size="large"
                placeholder="Tiempo de validez del código"
              />
            </Form.Item>
            
            <Form.Item 
              name="require_pin" 
              label={<strong>Proteger con PIN</strong>}
              valuePropName="checked"
            >
              <Switch checkedChildren="✓ Requerir PIN" unCheckedChildren="Sin PIN" />
            </Form.Item>
            
            <Button 
              type="primary" 
              htmlType="submit" 
              loading={shareLoading} 
              block 
              icon={<ShareAltOutlined />}
              size="large"
              style={{ borderRadius: 6, height: 40, fontSize: 15, fontWeight: 600 }}
            >
              Generar Código y QR
            </Button>
          </Form>
        ) : (
          <Space 
            direction="vertical" 
            style={{ width: '100%' }} 
            align="center"
            size={20}
          >
            <div style={{ textAlign: 'center' }}>
              <Text strong style={{ fontSize: 16, display: 'block', marginBottom: 12 }}>
                ✅ Código creado exitosamente
              </Text>
              <QRCode value={shareResult.qr_data} size={200} />
            </div>
            
            <Divider style={{ margin: '8px 0' }} />
            
            <div style={{ width: '100%', backgroundColor: '#f5f7fa', padding: 16, borderRadius: 6, textAlign: 'center' }}>
              <Text strong style={{ display: 'block', marginBottom: 8, fontSize: 12, color: '#6b7280' }}>CÓDIGO DE INVITACIÓN</Text>
              <Text 
                copyable 
                style={{ fontSize: 24, letterSpacing: 4, fontWeight: 700, fontFamily: 'monospace' }}
              >
                {shareResult.share_code}
              </Text>
            </div>
            
            {shareResult.pin && (
              <div style={{ width: '100%', backgroundColor: '#fef3c7', padding: 16, borderRadius: 6, textAlign: 'center' }}>
                <Text strong style={{ display: 'block', marginBottom: 8, fontSize: 12, color: '#92400e' }}>
                  <KeyOutlined /> PIN PROTECCIÓN
                </Text>
                <Text 
                  copyable 
                  style={{ fontSize: 20, letterSpacing: 2, fontWeight: 700, fontFamily: 'monospace' }}
                >
                  {shareResult.pin}
                </Text>
              </div>
            )}
            
            <div style={{ width: '100%', backgroundColor: '#e0f2fe', padding: 16, borderRadius: 6 }}>
              <Text strong style={{ display: 'block', marginBottom: 8, fontSize: 12, color: '#164e63' }}>
                <LinkOutlined /> URL DIRECTA
              </Text>
              <Text 
                copyable 
                style={{ fontSize: 12, wordBreak: 'break-all' }}
              >
                {shareResult.share_url}
              </Text>
            </div>
            
            <Space wrap style={{ width: '100%', justifyContent: 'center' }}>
              <Tag color="orange">
                ⏰ Expira: {new Date(shareResult.expires_at).toLocaleString('es-CL')}
              </Tag>
              <Tag color="blue">
                Máx. usos: {shareResult.max_uses}
              </Tag>
              <Tag color={shareResult.permissions === 'write' ? 'red' : 'green'}>
                {shareResult.permissions === 'write' ? '🎮 Control' : '👁️ Lectura'}
              </Tag>
            </Space>
          </Space>
        )}
      </Modal>

      <Modal 
        title="🔓 Unirse a dispositivo compartido" 
        open={joinModalVisible} 
        onCancel={() => { setJoinModalVisible(false); joinForm.resetFields(); }}
        footer={null}
        width={500}
        bodyStyle={{ padding: 24 }}
      >
        <Form form={joinForm} layout="vertical" onFinish={handleJoin}>
          <Alert
            type="info"
            showIcon
            message="Ingresa el código que te compartieron"
            description="Necesitarás el código de invitación y el PIN (si es requerido)"
            style={{ marginBottom: 16, borderRadius: 6 }}
          />
          
          <Form.Item 
            name="share_code" 
            label={<strong>Código de invitación</strong>}
            rules={[{ required: true, message: 'Ingresa el código' }]}
          >
            <Input 
              placeholder="ABC1234XYZ" 
              style={{ textTransform: 'uppercase', letterSpacing: 3, fontSize: 16, textAlign: 'center' }}
              size="large"
              maxLength={20}
              allowClear
            />
          </Form.Item>
          
          <Form.Item 
            name="pin" 
            label={<strong>PIN (solo si es requerido)</strong>}
          >
            <Input.Password 
              placeholder="1234" 
              maxLength={4}
              size="large"
            />
          </Form.Item>
          
          <Button 
            type="primary" 
            htmlType="submit" 
            loading={joinLoading} 
            block 
            icon={<UserAddOutlined />}
            size="large"
            style={{ borderRadius: 6, height: 40, fontSize: 15, fontWeight: 600 }}
          >
            Unirme al dispositivo
          </Button>
        </Form>
      </Modal>

      <Modal
        title={`📦 Actualizar firmware: ${otaTargetDevice?.name || ''}`}
        open={otaModalVisible}
        onCancel={() => setOtaModalVisible(false)}
        onOk={() => otaForm.submit()}
        okText="Enviar actualización OTA"
        confirmLoading={otaLoading}
        width={700}
        bodyStyle={{ padding: 24 }}
      >
        <Alert
          type="warning"
          showIcon
          message="⚙️ Actualizar firmware en el ESP32"
          description="El dispositivo descargará el nuevo firmware del URL y se reiniciará automáticamente. Asegúrate de que esté en línea y con batería suficiente."
          style={{ marginBottom: 16, borderRadius: 6 }}
        />
        
        <Form form={otaForm} layout="vertical" onFinish={handleSendOta}>
          <Form.Item
            name="firmware_url"
            label={<strong>URL del archivo .bin</strong>}
            rules={[
              { required: true, message: 'La URL es obligatoria' },
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve();
                  if (/^https?:\/\//i.test(value)) return Promise.resolve();
                  return Promise.reject(new Error('Debe comenzar con http:// o https://'));
                }
              }
            ]}
          >
            <Input 
              placeholder="https://mi-servidor.com/firmware/esp32_v2.bin" 
              size="large"
              allowClear
            />
          </Form.Item>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Form.Item 
              name="version" 
              label={<strong>Versión (opcional)</strong>}
            >
              <Input 
                placeholder="v1.2.0" 
                size="large"
                allowClear
              />
            </Form.Item>
            
            <Form.Item 
              name="checksum" 
              label={<strong>SHA256 (opcional)</strong>}
            >
              <Input 
                placeholder="hash..." 
                size="large"
                allowClear
              />
            </Form.Item>
          </div>
          
          <Form.Item 
            name="force" 
            label={<strong>Forzar actualización</strong>}
            valuePropName="checked"
          >
            <Switch 
              checkedChildren="Forzar (aunque sea misma versión)" 
              unCheckedChildren="Normal"
            />
          </Form.Item>
          
          <Alert
            type="info"
            showIcon
            message="🔧 Compatibilidad"
            description="El firmware debe tener compilado el soporte para OTA (comando 'ota_update'). Si tu dispositivo aún no lo tiene, primero carga una versión base por USB."
            style={{ borderRadius: 6 }}
          />
        </Form>
      </Modal>
    </div>
  );
}
